import { Box, Button, TextField, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppState } from 'store/store'
import useURLParams from 'utils/URLParamsContex'
import getEnvConfig from 'utils/getEnvConfig'

type SubmitState = 'idle' | 'success' | 'error' | 'rate_limited'

const Withdrawal = (): JSX.Element => {
    const { t } = useTranslation()
    const { B2B } = useURLParams()
    const envConfig = getEnvConfig()

    const { contactData, orderID, vzfID } = useSelector((state: AppState) => ({
        contactData: state.contactData,
        orderID: state.generalState.orderID,
        vzfID: state.generalState.vzfID,
    }))

    const [firstName, setFirstName] = useState(contactData.personalName || '')
    const [lastName, setLastName] = useState(contactData.personalLastName || '')
    const [email, setEmail] = useState(contactData.personalEmail || '')
    const [contractIdentifier, setContractIdentifier] = useState(orderID || vzfID || '')
    const [message, setMessage] = useState('')
    const [submitState, setSubmitState] = useState<SubmitState>('idle')
    const [loading, setLoading] = useState(false)

    const validationError = useMemo(() => {
        if (B2B) return t('withdrawalStrings.b2bNotSupported')
        if (!firstName.trim() || !lastName.trim()) return t('withdrawalStrings.requiredName')
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) return t('withdrawalStrings.invalidEmail')
        if (!contractIdentifier.trim()) return t('withdrawalStrings.requiredContract')
        return ''
    }, [B2B, contractIdentifier, email, firstName, lastName, t])

    const onSubmit = async (): Promise<void> => {
        if (validationError || loading) return
        setSubmitState('idle')
        setLoading(true)

        const withdrawalURL =
            process.env.NODE_ENV !== 'production'
                ? `http://${envConfig.Domain}:${envConfig.HTTPListen}/withdrawal`
                : '/withdrawal'

        try {
            const response = await fetch(withdrawalURL, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    email: email.trim(),
                    contractIdentifier: contractIdentifier.trim(),
                    bookingID: orderID || '',
                    vzfID: vzfID || '',
                    message: message.trim(),
                }),
            })

            const data = await response.json()
            if (data && data.status === 'success') {
                setSubmitState('success')
                return
            }
            if (data && data.message === 'rate_limited') {
                setSubmitState('rate_limited')
                return
            }
            setSubmitState('error')
        } catch {
            setSubmitState('error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box
            p={3}
            minHeight={{ xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 194px)' }}
            display={'flex'}
            justifyContent={'center'}
        >
            <Box width={'100%'} maxWidth={680}>
                <Typography variant={'h1'}>{t('withdrawalStrings.header')}</Typography>
                <Box mb={2}>
                    <Typography variant={'body1'}>{t('withdrawalStrings.subheader')}</Typography>
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={t('withdrawalStrings.firstName')}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={t('withdrawalStrings.lastName')}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                    />
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={t('withdrawalStrings.email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={t('withdrawalStrings.contractIdentifier')}
                        helperText={t('withdrawalStrings.contractIdentifierHint')}
                        value={contractIdentifier}
                        onChange={(e) => setContractIdentifier(e.target.value)}
                    />
                </Box>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={t('withdrawalStrings.messageOptional')}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                </Box>
                <Box mb={2}>
                    <Typography variant={'body2'}>{t('withdrawalStrings.confirmationHint')}</Typography>
                </Box>
                {!!validationError && <Alert severity="warning">{validationError}</Alert>}
                {submitState === 'success' && <Alert severity="success">{t('withdrawalStrings.success')}</Alert>}
                {submitState === 'error' && <Alert severity="error">{t('withdrawalStrings.error')}</Alert>}
                {submitState === 'rate_limited' && <Alert severity="error">{t('withdrawalStrings.rateLimited')}</Alert>}
                <Box mt={2}>
                    <Button
                        color={'primary'}
                        variant={'contained'}
                        onClick={onSubmit}
                        disabled={!!validationError || loading}
                    >
                        {t('withdrawalStrings.submit')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export default Withdrawal
